/* Created by Interactive 63 - https://interactive63.com */
/**
 * A button that opens a drop-down list of commands, used for the File and Edit menus. The list
 * is a popover of plain buttons: it opens with Enter or Space, moves with the arrow keys and
 * closes with Escape. Disabled states are worked out each time the list opens.
 */
import { h } from './dom';
import { createPopover } from './popover';

export interface MenuCommand {
  label: string;
  /** The keyboard shortcut shown beside the label, if there is one. */
  shortcut?: string;
  /** Asked each time the menu opens; true greys the command out. */
  disabled?: () => boolean;
  onSelect: () => void;
}

export type MenuEntry = MenuCommand | 'separator';

/** Builds the button and its drop-down. The returned element holds both. */
export function createMenuButton(label: string, entries: readonly MenuEntry[]): HTMLElement {
  const button = h(
    'button',
    { type: 'button', 'aria-haspopup': 'menu', 'aria-expanded': 'false', class: 'menu-trigger' },
    label,
    h('span', { class: 'menu-caret', 'aria-hidden': 'true' }, '▾'),
  );
  const list = h('div', { class: 'menu-list menu-commands', role: 'menu', 'aria-label': label });
  const popover = createPopover(button, list, label);

  const items = entries.map((entry) => {
    if (entry === 'separator') {
      list.append(h('hr', { role: 'separator' }));
      return null;
    }
    const item = h(
      'button',
      { type: 'button', role: 'menuitem' },
      h('span', null, entry.label),
      entry.shortcut ? h('kbd', null, entry.shortcut) : null,
    );
    item.addEventListener('click', () => {
      popover.close();
      entry.onSelect();
    });
    list.append(item);
    return { entry, item };
  });

  /** The commands that can currently be chosen. */
  const enabled = (): HTMLButtonElement[] =>
    Array.from(list.querySelectorAll<HTMLButtonElement>('button[role=menuitem]')).filter(
      (b) => !b.disabled,
    );

  list.addEventListener('keydown', (event) => {
    const options = enabled();
    const at = options.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown') options[(at + 1) % options.length]?.focus();
    else if (event.key === 'ArrowUp') options[(at - 1 + options.length) % options.length]?.focus();
    else return;
    event.preventDefault();
  });

  button.addEventListener('click', () => {
    if (popover.isOpen()) {
      popover.close();
      return;
    }
    for (const found of items) {
      if (found) found.item.disabled = found.entry.disabled?.() ?? false;
    }
    popover.open();
    enabled()[0]?.focus();
  });
  popover.el.addEventListener('toggle', () => {
    button.setAttribute('aria-expanded', String(popover.isOpen()));
  });

  return h('span', { class: 'menu-wrap' }, button, popover.el);
}
